import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Icon } from './Icon'
import { IconButton } from './IconButton'
import type { IconName } from './icons'

export type ToastVariant = 'success' | 'error' | 'info' | 'warning'

export interface ToastOptions {
  variant: ToastVariant
  title: string
  description?: string
  /** The same action must also exist elsewhere on the screen (the toast may vanish first). */
  action?: { label: string; onClick: () => void }
  /** ms; default 5000. Errors and toasts with an action never close by themselves. */
  duration?: number
}

interface ToastItem extends ToastOptions {
  id: number
}

interface ToastApi {
  show: (options: ToastOptions) => number
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastApi | null>(null)

const MAX_VISIBLE = 3
const DEFAULT_DURATION = 5000

const ICON: Record<ToastVariant, IconName> = {
  success: 'success',
  error: 'error',
  info: 'info',
  warning: 'warning',
}

/** Toast API. Must be inside <ToastProvider> (mounted once by the app). */
export function useToast(): ToastApi {
  const api = use(ToastContext)
  if (!api) throw new Error('useToast precisa estar dentro de <ToastProvider>')
  return api
}

/**
 * Mounts the two live regions once (polite for success/info/warning, alert for errors) and
 * stacks up to 3 toasts, newest at the bottom. Above the BottomNav and the safe area on phones.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const nextId = useRef(1)

  const dismiss = useCallback(
    (id: number) => setItems((list) => list.filter((t) => t.id !== id)),
    [],
  )

  const show = useCallback((options: ToastOptions) => {
    const id = nextId.current++
    setItems((list) => [...list, { ...options, id }].slice(-MAX_VISIBLE))
    return id
  }, [])

  const api = useMemo(() => ({ show, dismiss }), [show, dismiss])
  const polite = items.filter((t) => t.variant !== 'error')
  const assertive = items.filter((t) => t.variant === 'error')

  return (
    <ToastContext value={api}>
      {children}
      <div className="ui-toasts">
        <div className="ui-toasts__region" aria-live="polite">
          {polite.map((t) => (
            <ToastView key={t.id} toast={t} onDismiss={dismiss} />
          ))}
        </div>
        <div className="ui-toasts__region" role="alert">
          {assertive.map((t) => (
            <ToastView key={t.id} toast={t} onDismiss={dismiss} />
          ))}
        </div>
      </div>
    </ToastContext>
  )
}

function ToastView({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  const [paused, setPaused] = useState(false)
  const sticky = toast.variant === 'error' || toast.action !== undefined

  useEffect(() => {
    if (sticky || paused) return
    const timer = window.setTimeout(() => onDismiss(toast.id), toast.duration ?? DEFAULT_DURATION)
    return () => window.clearTimeout(timer)
  }, [sticky, paused, toast.id, toast.duration, onDismiss])

  return (
    <div
      className={`ui-toast ui-toast--${toast.variant}`}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onDismiss(toast.id)
      }}
    >
      <span className="ui-toast__icon">
        <Icon name={ICON[toast.variant]} />
      </span>
      <div className="ui-toast__content">
        <p className="ui-toast__title">{toast.title}</p>
        {toast.description && <p className="ui-toast__description">{toast.description}</p>}
        {toast.action && (
          <button
            type="button"
            className="ui-toast__action"
            onClick={() => {
              toast.action?.onClick()
              onDismiss(toast.id)
            }}
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <IconButton icon="close" label="Fechar notificação" onClick={() => onDismiss(toast.id)} />
    </div>
  )
}
